import { ComponentType, lazy } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { Settings } from './pages/Settings';
import WhiteLabelLayout from './containers/WhiteLabelLayout';
import SubscriptionPayment from './pages/subscription/SubscriptionPayment';
import { ShopPaymentSettings } from './pages/ShopPaymentSettings';
import { getTranslationKey } from './modules/utils';

const UserDashboard = lazy(() => import('./views/User/Dashboard'));
const AdminDashboard = lazy(() => import('./views/Admin/Dashboard'));
const AdminUserView = lazy(() => import('./elements/Admin/User'));
const CollectionsOverview = lazy(() => import('./pages/Collections'));
const CollectionEdit = lazy(() => import('./pages/CollectionEdit'));
const Portfolios = lazy(() => import('./pages/Portfolios'));
const PortfolioEdit = lazy(() => import('./pages/PortfolioEdit'));
const Slideshow = lazy(() => import('./pages/collectionEdit/slideshow/Slideshow'));
const Account = lazy(() => import('./views/User/Account'));
const Pricing = lazy(() => import('./views/Pricing/ScrAppBookPricing'));
const Subscription = lazy(() => import('./pages/Subscription'));
const Referral = lazy(() => import('./views/Subscription/Referral'));
const CollectionPresets = lazy(() => import('./pages/CollectionPresets'));
const Shop = lazy(() => import('./pages/Shop'));
// TODO: remove after testing
const ShopPricing = lazy(() => import('./pages/ShopPricing'));
// TODO: remove after testing
const ShopDigitalPricing = lazy(() => import('./pages/ShopDigitalPricing'));
const newShopPricing = lazy(() => import('./pages/newShowPricing/ShopPricing'));
const ShopCoupons = lazy(() => import('./pages/ShopCoupons'));
const ShopOrders = lazy(() => import('./pages/ShopOrders'));
const OverviewTab = lazy(() => import('./pages/OverviewTab'));
const SalesAutomation = lazy(() => import('./pages/SalesAutomation'));

export interface WhiteLabelRouteConfig {
  id: string;
  path: string;
  name: string;
  component: ComponentType<RouteComponentProps<any>> | ComponentType<any>;
  exact?: boolean;
}

// https://github.com/ReactTraining/react-router/tree/master/packages/react-router-config
const getWhiteLabelRoutes = (): WhiteLabelRouteConfig[] => [
  {
    id: 'home',
    path: '/',
    exact: true,
    name: 'Home',
    component: WhiteLabelLayout
  },
  {
    id: 'referral',
    path: '/referral',
    name: getTranslationKey('referralProgram.menu'),
    component: Referral,
    exact: true
  },
  {
    path: '/dashboard',
    id: 'dashboard',
    name: getTranslationKey('userDashboard.title'),
    component: UserDashboard,
    exact: true
  },
  {
    path: '/admin/dashboard',
    id: 'admin-dashboard',
    name: getTranslationKey('adminDashboard.title'),
    component: AdminDashboard,
    exact: true
  },
  {
    path: '/admin/user/show/:id',
    id: 'admin-user-view',
    name: getTranslationKey('adminDashboard.user'),
    component: AdminUserView
  },
  {
    path: '/collections',
    id: 'collections',
    name: getTranslationKey('myCollectionsShort'),
    component: CollectionsOverview,
    exact: true
  },
  {
    id: 'collection-edit',
    path: '/collection/edit/:collection_id/:tab?',
    name: getTranslationKey('editGallery'),
    component: CollectionEdit,
    exact: true
  },
  {
    id: 'collection-edit-slideshow',
    path: '/collection/edit/:collection_id/:tab?/slideshow/:slideshow_id',
    name: getTranslationKey('slideshow.heading'),
    component: Slideshow,
    exact: true
  },
  {
    id: 'portfolios',
    path: '/portfolios',
    name: getTranslationKey('portfolio.portfolios'),
    component: Portfolios,
    exact: true
  },
  {
    id: 'portfolio-edit',
    path: '/portfolios/edit/:portfolio_id?',
    name: getTranslationKey('portfolio.title'),
    component: PortfolioEdit,
    exact: true
  },
  {
    id: 'gallery-presets',
    path: '/gallerypresets',
    exact: true,
    name: getTranslationKey('galleryPresets.route'),
    component: CollectionPresets
  },
  {
    id: 'settings',
    path: '/settings',
    exact: true,
    name: getTranslationKey('settings'),
    component: Settings
  },
  {
    id: 'account',
    path: '/account',
    exact: true,
    name: getTranslationKey('account'),
    component: Account
  },
  {
    id: 'subscription-pricing-old',
    path: '/subscription/pricing/old',
    exact: true,
    name: getTranslationKey('pricingPage'),
    component: Pricing
  },
  {
    id: 'subscription-pricing',
    path: '/subscription/pricing',
    exact: true,
    name: getTranslationKey('pricingPage'),
    component: Subscription
  },
  {
    id: 'subscription-payment',
    path: '/subscription/pricing/payment',
    exact: true,
    name: getTranslationKey('paymentPage'),
    component: SubscriptionPayment
  },
  {
    id: 'payment-method-setup',
    path: '/payment-method-setup',
    exact: true,
    name: getTranslationKey('paymentPage'),
    component: SubscriptionPayment
  },
  {
    id: 'shop',
    path: '/shop',
    exact: true,
    name: getTranslationKey('shop.shopSubMenuLabel'),
    component: Shop
  },
  {
    id: 'shop-prices',
    path: '/shop/newPrices', // todo change to component
    exact: true,
    name: getTranslationKey('shop.priceMenuLabel'),
    component: newShopPricing
  },
  {
    id: 'shop-prices', // TODO: remove after testing also remove texts labels
    path: '/shop/prices',
    exact: true,
    name: getTranslationKey('shop.priceMenuLabelOld'),
    component: ShopPricing
  },
  {
    id: 'digital-pricing-list',
    path: '/shop/digital-pricing-list',
    exact: true,
    name: getTranslationKey('shop.digitalPricingListLabel'),
    component: ShopDigitalPricing
  },
  {
    id: 'shop-sales-automation',
    path: '/shop/sales-automation',
    exact: true,
    name: getTranslationKey('shop.sales-automation.title'),
    component: SalesAutomation
  },
  {
    id: 'coupons',
    path: '/shop/coupons',
    exact: true,
    name: getTranslationKey('shop.couponsMenuLabel'),
    component: ShopCoupons
  },
  {
    id: 'orders',
    path: '/shop/orders',
    exact: true,
    name: getTranslationKey('shop.ordersMenuLabel'),
    component: ShopOrders
  },
  {
    id: 'subscription-overview',
    path: '/subscription',
    exact: true,
    name: getTranslationKey('overview'),
    component: OverviewTab
  },
  {
    id: 'shop-payment-settings',
    path: '/shop/payment-settings',
    exact: true,
    name: getTranslationKey('shop.paymentSettings.pageTitle'),
    component: ShopPaymentSettings
  }
];

export default getWhiteLabelRoutes;
